import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { useUploadFileMutation } from "@dashboard/files/api";
import { FileContentType, FileInfo } from "@dashboard/files/types";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { forbiddenFileTypes, maxFileSize } from "./useFileUpload";

type Options = {
    extensions?: string[];
    contentTypes?: FileContentType[];
};

const getUploadContentType = (file: File, contentTypes: FileContentType[] | undefined) => {
    if (!contentTypes?.length || contentTypes.some((item) => item === file.type)) {
        return file.type;
    }
    return contentTypes[0];
};

export const useWorkspaceFileUpload = (options: Options) => {
    const { t } = useTranslation(["common", "pages"]);
    const uploadRef = React.useRef<AbortController | null>(null);
    const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");
    const isExpired = useCurrentSubscriptionExpired();

    const cannotUploadFile = workspaceAbility(workspace).cannot("manage", "File");

    const getDisabledReason = () => {
        if (isExpired) {
            return t("pages:filesPage.tooltips.subscriptionExpired");
        }
        if (cannotUploadFile) {
            return t("common:section.uploadField.noPermission");
        }
        if (isOverlimit) {
            return t("pages:filesPage.tooltips.overlimit");
        }
        return undefined;
    };

    const upload = async (file: File): Promise<FileInfo | undefined> => {
        const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (options.extensions && !options.extensions.includes(extension)) {
            showErrorToast(
                t("common:section.uploadField.wrongExtension", {
                    extensions: options.extensions.map((item) => `.${item}`).join(", "),
                }),
            );
            return undefined;
        }
        if (forbiddenFileTypes.includes(extension)) {
            showErrorToast(t("common:section.uploadField.unsupportedFileType"));
            return undefined;
        }
        if (file.size > maxFileSize) {
            showErrorToast(t("common:section.uploadField.maxSizeExceeded"));
            return undefined;
        }
        if (!workspaceId) {
            return undefined;
        }

        const contentType = getUploadContentType(file, options.contentTypes);
        const fileToUpload = contentType === file.type ? file : new File([file], file.name, { type: contentType });
        uploadRef.current = new AbortController();
        try {
            const uploadedFile = await uploadFile({
                workspaceId,
                filename: fileToUpload.name,
                fileToUpload,
                controller: uploadRef.current,
                groupId: null,
                onUploadProgress: () => {},
                setUploadedFileIds: () => {},
            }).unwrap();
            showSuccessToast(t("common:section.uploadField.toast.fileUploadedSuccess"));
            return uploadedFile;
        } catch {
            // The upload mutation reports the failure itself: an error toast, or a warning when it was cancelled.
            return undefined;
        } finally {
            uploadRef.current = null;
        }
    };

    // Whoever started the upload is the only one waiting for it, so it is cancelled once the component
    // holding this hook goes away rather than finishing with nobody left to see the result.
    React.useEffect(() => () => uploadRef.current?.abort(), []);

    return { upload, isUploading, disabledReason: getDisabledReason() };
};
